
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { formatCertificateNumber } from '@/utils/certificateUtils';
import { format } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow 
} from '@/components/ui/table';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertTriangle,
  Clock,
  FileCheck,
  FileText,
  Filter,
  Loader2,
  User
} from 'lucide-react';
import { Button } from '@/components/ui/button';

interface CertificateAuditTrailProps {
  certificateNumber?: string;
  certificateId?: string;
  limit?: number;
  showFilters?: boolean;
}

interface AuditRecord {
  id: string;
  certificate_number: string;
  action_type: 'created' | 'verified' | 'reported' | 'revoked' | 'updated' | 'viewed';
  performed_at: string;
  performed_by?: string;
  admin_name?: string;
  details?: any;
  verification_status?: string;
  reporting_status?: string;
}

export function CertificateAuditTrail({
  certificateNumber,
  certificateId,
  limit = 50,
  showFilters = true
}: CertificateAuditTrailProps) {
  const [actionTypeFilter, setActionTypeFilter] = useState<string | undefined>(undefined);
  
  // Fetch certificate audit trail data
  const { data: auditRecords, isLoading, error } = useQuery({
    queryKey: ['certificateAudit', certificateNumber, certificateId, actionTypeFilter, limit],
    queryFn: async () => {
      let query = supabase
        .from('certificate_verification_history')
        .select(`
          id,
          certificate_number,
          verification_status,
          verification_date as performed_at,
          verified_by as performed_by,
          notes as details,
          admin_users (full_name)
        `)
        .order('verification_date', { ascending: false });
        
      if (certificateNumber) {
        query = query.eq('certificate_number', certificateNumber);
      }
      
      if (certificateId) {
        query = query.eq('inventory_id', certificateId);
      }
      
      if (actionTypeFilter) {
        // Apply any additional filters if needed
      }
      
      const { data: verificationHistory, error: verificationError } = await query.limit(limit);
      
      if (verificationError) throw verificationError;
      
      // Format the records to match our audit record interface
      const formattedRecords: AuditRecord[] = (verificationHistory || []).map((record: any) => ({
        id: record.id,
        certificate_number: record.certificate_number,
        action_type: 'verified',
        performed_at: record.performed_at,
        performed_by: record.performed_by,
        admin_name: record.admin_users?.full_name || 'System',
        details: record.details,
        verification_status: record.verification_status
      }));
      
      return formattedRecords;
    }
  });
  
  const renderActionBadge = (action: string) => {
    switch (action) {
      case 'created':
        return <Badge variant="outline" className="bg-blue-50 text-blue-700">Created</Badge>;
      case 'verified':
        return <Badge variant="outline" className="bg-green-50 text-green-700">Verified</Badge>;
      case 'reported':
        return <Badge variant="outline" className="bg-purple-50 text-purple-700">Reported</Badge>;
      case 'revoked':
        return <Badge variant="outline" className="bg-red-50 text-red-700">Revoked</Badge>;
      case 'updated':
        return <Badge variant="outline" className="bg-amber-50 text-amber-700">Updated</Badge>;
      case 'viewed':
        return <Badge variant="outline" className="bg-gray-50 text-gray-700">Viewed</Badge>;
      default:
        return <Badge variant="outline">{action}</Badge>;
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-red-500">
        <AlertTriangle className="inline-block mr-2 h-5 w-5" />
        Error loading audit trail: {(error as Error).message}
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-start">
          <div>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Certificate Audit Trail
            </CardTitle>
            <CardDescription>
              {certificateNumber 
                ? `History for certificate ${formatCertificateNumber(certificateNumber)}` 
                : 'Recent certificate audit activity'}
            </CardDescription>
          </div>
          {showFilters && (
            <div className="flex gap-2">
              <Select
                value={actionTypeFilter}
                onValueChange={setActionTypeFilter}
              >
                <SelectTrigger className="w-[180px]">
                  <div className="flex items-center gap-2">
                    <Filter className="h-4 w-4" />
                    <span>{actionTypeFilter || 'All Actions'}</span>
                  </div>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={undefined}>All Actions</SelectItem>
                  <SelectItem value="created">Created</SelectItem>
                  <SelectItem value="verified">Verified</SelectItem>
                  <SelectItem value="reported">Reported</SelectItem>
                  <SelectItem value="revoked">Revoked</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {auditRecords && auditRecords.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date/Time</TableHead>
                {!certificateNumber && <TableHead>Certificate</TableHead>}
                <TableHead>Action</TableHead>
                <TableHead>Performed By</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Details</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {auditRecords.map((record) => (
                <TableRow key={record.id}>
                  <TableCell className="whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      {format(new Date(record.performed_at), 'MMM d, yyyy h:mm a')}
                    </div>
                  </TableCell>
                  {!certificateNumber && (
                    <TableCell className="font-medium">
                      {formatCertificateNumber(record.certificate_number)}
                    </TableCell>
                  )}
                  <TableCell>
                    {renderActionBadge(record.action_type)}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-muted-foreground" />
                      {record.admin_name || 'System'}
                    </div>
                  </TableCell>
                  <TableCell>
                    {record.verification_status === 'verified' ? (
                      <Badge variant="success" className="flex gap-1 items-center">
                        <FileCheck className="h-3 w-3" /> Verified
                      </Badge>
                    ) : record.reporting_status === 'reported' ? (
                      <Badge variant="success" className="flex gap-1 items-center">
                        <FileCheck className="h-3 w-3" /> Reported
                      </Badge>
                    ) : (
                      <Badge variant="outline">
                        {record.verification_status || record.reporting_status || 'N/A'}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="max-w-[200px] truncate">
                    {record.details || '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            {certificateNumber 
              ? `No audit records found for certificate ${formatCertificateNumber(certificateNumber)}`
              : 'No certificate audit records found'}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
